import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Brain, Users, Globe, Zap, BookOpen, Star, ArrowRight, Play, ChevronDown,
  Sparkles, GraduationCap, BarChart3, Shield, ClipboardList, FileText,
  Calendar, MessageCircle, Bus, Wallet, Receipt, Package, FolderOpen,
  Smartphone, PenLine, CalendarCheck, Check, Monitor, UserCheck, CreditCard,
  TrendingUp, DollarSign, Bell, Eye, Heart, Twitter, Linkedin, Facebook, Youtube,
} from 'lucide-react';
import { LanguageSwitcher } from '@/components/ui/language-switcher';

function AppLogo({ size = 36 }: { size?: number }) {
  return (
    <img
      src="/logo-icon.svg"
      alt=""
      width={size}
      height={size}
      className="object-contain"
    />
  );
}

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

function StatCounter({ value, label }: { value: string; label: string }) {
  const { ref, visible } = useScrollReveal();
  const numMatch = value.match(/^([\d.]+)/);
  const suffix = value.replace(/^[\d.]+/, '');
  const target = numMatch ? parseFloat(numMatch[1]) : 0;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!visible || !target) return;
    let start = 0;
    const step = target / 40;
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(id); }
      else setCount(Math.floor(start * 10) / 10);
    }, 30);
    return () => clearInterval(id);
  }, [visible, target]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold font-poppins mb-2 tabular-nums">
        {target ? (Number.isInteger(target) ? Math.round(count) : count.toFixed(1)) : value.split('/')[0]}
        {suffix}
      </div>
      <div className="text-sm md:text-base opacity-80 font-medium">{label}</div>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const features = [
    { icon: Brain, title: t('landing.features.aiLearning.title'), desc: t('landing.features.aiLearning.description'), badge: t('landing.features.aiLearning.badge'), color: 'from-primary-500 to-blue-500' },
    { icon: Users, title: t('landing.features.teacherCopilot.title'), desc: t('landing.features.teacherCopilot.description'), badge: t('landing.features.teacherCopilot.badge'), color: 'from-secondary-500 to-secondary-500' },
    { icon: Globe, title: t('landing.features.multilingualSupport.title'), desc: t('landing.features.multilingualSupport.description'), badge: t('landing.features.multilingualSupport.badge'), color: 'from-emerald-500 to-primary-500' },
    { icon: BarChart3, title: t('landing.features.analytics.title'), desc: t('landing.features.analytics.description'), badge: t('landing.features.analytics.badge'), color: 'from-amber-500 to-orange-500' },
    { icon: BookOpen, title: t('landing.features.interactiveContent.title'), desc: t('landing.features.interactiveContent.description'), badge: t('landing.features.interactiveContent.badge'), color: 'from-rose-500 to-pink-500' },
    { icon: Shield, title: t('landing.features.selIntegration.title'), desc: t('landing.features.selIntegration.description'), badge: t('landing.features.selIntegration.badge'), color: 'from-secondary-500 to-blue-600' },
  ];

  const stats = [
    { value: '500+', label: t('landing.stats.schoolsUsing') },
    { value: '50K+', label: t('landing.stats.studentsManaged') },
    { value: '94%', label: t('landing.stats.avgAttendance') },
    { value: '4.9/5', label: t('landing.stats.directorRating') },
  ];

  const personas = [
    {
      emoji: '\u{1F393}', title: t('landing.personas.student.title'), desc: t('landing.personas.student.desc'),
      benefits: [t('landing.personas.student.b1'), t('landing.personas.student.b2'), t('landing.personas.student.b3')],
      color: 'from-primary-500 to-blue-500', route: '/onboard/quick-student', cta: t('landing.personas.student.cta'),
    },
    {
      emoji: '\u{1F4DA}', title: t('landing.personas.teacher.title'), desc: t('landing.personas.teacher.desc'),
      benefits: [t('landing.personas.teacher.b1'), t('landing.personas.teacher.b2'), t('landing.personas.teacher.b3')],
      color: 'from-emerald-500 to-primary-500', route: '/onboard/individual?role=teacher', cta: t('landing.personas.teacher.cta'),
    },
    {
      emoji: '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}', title: t('landing.personas.parent.title'), desc: t('landing.personas.parent.desc'),
      benefits: [t('landing.personas.parent.b1'), t('landing.personas.parent.b2'), t('landing.personas.parent.b3')],
      color: 'from-secondary-500 to-secondary-500', route: '/onboard/individual?role=parent', cta: t('landing.personas.parent.cta'),
    },
    {
      emoji: '\u{1F3EB}', title: t('landing.personas.school.title'), desc: t('landing.personas.school.desc'),
      benefits: [t('landing.personas.school.b1'), t('landing.personas.school.b2'), t('landing.personas.school.b3')],
      color: 'from-secondary-500 to-blue-600', route: '/onboard/school-admin', cta: t('landing.personas.school.cta'),
    },
  ];

  const moduleCategories = [
    {
      key: 'academic',
      label: t('landing.schoolMgmt.catAcademic'),
      color: 'from-primary-500 to-blue-500',
      modules: [
        { icon: ClipboardList, label: t('landing.schoolMgmt.attendance'), desc: t('landing.schoolMgmt.attendanceDesc') },
        { icon: BarChart3, label: t('landing.schoolMgmt.grades'), desc: t('landing.schoolMgmt.gradesDesc') },
        { icon: FileText, label: t('landing.schoolMgmt.reportCards'), desc: t('landing.schoolMgmt.reportCardsDesc') },
        { icon: Calendar, label: t('landing.schoolMgmt.timetables'), desc: t('landing.schoolMgmt.timetablesDesc') },
        { icon: PenLine, label: t('landing.schoolMgmt.homework'), desc: t('landing.schoolMgmt.homeworkDesc') },
      ],
    },
    {
      key: 'admin',
      label: t('landing.schoolMgmt.catAdmin'),
      color: 'from-secondary-500 to-secondary-500',
      modules: [
        { icon: Wallet, label: t('landing.schoolMgmt.fees'), desc: t('landing.schoolMgmt.feesDesc') },
        { icon: Receipt, label: t('landing.schoolMgmt.payroll'), desc: t('landing.schoolMgmt.payrollDesc') },
        { icon: Bus, label: t('landing.schoolMgmt.transport'), desc: t('landing.schoolMgmt.transportDesc') },
        { icon: Package, label: t('landing.schoolMgmt.inventory'), desc: t('landing.schoolMgmt.inventoryDesc') },
        { icon: FolderOpen, label: t('landing.schoolMgmt.documents'), desc: t('landing.schoolMgmt.documentsDesc') },
      ],
    },
    {
      key: 'communication',
      label: t('landing.schoolMgmt.catComm'),
      color: 'from-emerald-500 to-primary-500',
      modules: [
        { icon: MessageCircle, label: t('landing.schoolMgmt.communication'), desc: t('landing.schoolMgmt.communicationDesc') },
        { icon: Smartphone, label: t('landing.schoolMgmt.sms'), desc: t('landing.schoolMgmt.smsDesc') },
        { icon: Eye, label: t('landing.schoolMgmt.parentPortal'), desc: t('landing.schoolMgmt.parentPortalDesc') },
        { icon: CalendarCheck, label: t('landing.schoolMgmt.appointments'), desc: t('landing.schoolMgmt.appointmentsDesc') },
      ],
    },
    {
      key: 'intelligence',
      label: t('landing.schoolMgmt.catIntel'),
      color: 'from-amber-500 to-orange-500',
      modules: [
        { icon: Shield, label: t('landing.schoolMgmt.analytics'), desc: t('landing.schoolMgmt.analyticsDesc') },
        { icon: Brain, label: t('landing.schoolMgmt.aiTutor'), desc: t('landing.schoolMgmt.aiTutorDesc') },
        { icon: GraduationCap, label: t('landing.schoolMgmt.examPrep'), desc: t('landing.schoolMgmt.examPrepDesc') },
        { icon: TrendingUp, label: t('landing.schoolMgmt.tracking'), desc: t('landing.schoolMgmt.trackingDesc') },
      ],
    },
  ];

  const viewTabs = [
    { key: 'admin', label: t('landing.seeItInAction.tabAdmin'), icon: Monitor },
    { key: 'teacher', label: t('landing.seeItInAction.tabTeacher'), icon: BookOpen },
    { key: 'parent', label: t('landing.seeItInAction.tabParent'), icon: Heart },
    { key: 'accounting', label: t('landing.seeItInAction.tabAccounting'), icon: DollarSign },
  ];

  const testimonials = [
    { quote: t('landing.testimonials.t1.quote'), name: t('landing.testimonials.t1.name'), role: t('landing.testimonials.t1.role'), color: 'from-primary-500 to-blue-500', flag: '\u{1F1E8}\u{1F1F2}' },
    { quote: t('landing.testimonials.t2.quote'), name: t('landing.testimonials.t2.name'), role: t('landing.testimonials.t2.role'), color: 'from-secondary-500 to-secondary-500', flag: '\u{1F1EB}\u{1F1F7}' },
    { quote: t('landing.testimonials.t3.quote'), name: t('landing.testimonials.t3.name'), role: t('landing.testimonials.t3.role'), color: 'from-emerald-500 to-primary-500', flag: '\u{1F1F8}\u{1F1F3}' },
    { quote: t('landing.testimonials.t4.quote'), name: t('landing.testimonials.t4.name'), role: t('landing.testimonials.t4.role'), color: 'from-amber-500 to-orange-500', flag: '\u{1F1E8}\u{1F1EE}' },
    { quote: t('landing.testimonials.t5.quote'), name: t('landing.testimonials.t5.name'), role: t('landing.testimonials.t5.role'), color: 'from-rose-500 to-pink-500', flag: '\u{1F1EC}\u{1F1E6}' },
  ];

  const pricingPlans = [
    {
      name: t('landing.pricing.starter.name'),
      price: t('landing.pricing.starter.price'),
      period: t('landing.pricing.starter.period'),
      features: [t('landing.pricing.starter.f1'), t('landing.pricing.starter.f2'), t('landing.pricing.starter.f3'), t('landing.pricing.starter.f4')],
      cta: t('landing.pricing.starter.cta'),
      popular: false,
    },
    {
      name: t('landing.pricing.pro.name'),
      price: t('landing.pricing.pro.price'),
      period: t('landing.pricing.pro.period'),
      features: [t('landing.pricing.pro.f1'), t('landing.pricing.pro.f2'), t('landing.pricing.pro.f3'), t('landing.pricing.pro.f4'), t('landing.pricing.pro.f5')],
      cta: t('landing.pricing.pro.cta'),
      popular: true,
    },
    {
      name: t('landing.pricing.enterprise.name'),
      price: t('landing.pricing.enterprise.price'),
      period: t('landing.pricing.enterprise.period'),
      features: [t('landing.pricing.enterprise.f1'), t('landing.pricing.enterprise.f2'), t('landing.pricing.enterprise.f3'), t('landing.pricing.enterprise.f4'), t('landing.pricing.enterprise.f5')],
      cta: t('landing.pricing.enterprise.cta'),
      popular: false,
    },
  ];

  const steps = [
    { num: '01', title: t('landing.steps.s1.title'), desc: t('landing.steps.s1.desc') },
    { num: '02', title: t('landing.steps.s2.title'), desc: t('landing.steps.s2.desc') },
    { num: '03', title: t('landing.steps.s3.title'), desc: t('landing.steps.s3.desc') },
  ];

  const feat1 = useScrollReveal();
  const feat2 = useScrollReveal();
  const personaReveal = useScrollReveal();
  const schoolReveal = useScrollReveal();
  const trustReveal = useScrollReveal();
  const stepsReveal = useScrollReveal();
  const ctaReveal = useScrollReveal();
  const actionReveal = useScrollReveal();
  const pricingReveal = useScrollReveal();
  const testimonialReveal = useScrollReveal();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-inter antialiased">
      {/* Nav */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 dark:bg-gray-950/90 backdrop-blur-xl shadow-sm border-b border-gray-100 dark:border-gray-800' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2.5 group">
            <AppLogo size={34} />
            <span className="text-xl font-bold font-poppins tracking-tight bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {t('common.appName')}
            </span>
          </a>

          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher variant="ghost" showLabel />
            <button onClick={() => navigate('/demo')} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              {t('landing.nav.tryDemo')}
            </button>
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              {t('landing.nav.signIn')}
            </button>
            <button onClick={() => navigate('/onboard')} className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-secondary-600 rounded-xl hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-200 active:scale-[0.98]">
              {t('landing.nav.getStarted')}
            </button>
          </div>

          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileMenu ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></> : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
            </svg>
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 px-5 py-4 space-y-2 animate-in slide-in-from-top-2">
            <button onClick={() => { navigate('/demo'); setMobileMenu(false); }} className="w-full text-left px-4 py-3 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">{t('landing.nav.tryDemo')}</button>
            <button onClick={() => { navigate('/login'); setMobileMenu(false); }} className="w-full text-left px-4 py-3 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">{t('landing.nav.signIn')}</button>
            <button onClick={() => { navigate('/onboard'); setMobileMenu(false); }} className="w-full px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-secondary-600 rounded-xl">{t('landing.nav.getStarted')}</button>
            <div className="pt-2"><LanguageSwitcher variant="ghost" showLabel /></div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-primary-400/20 to-secondary-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-gradient-to-br from-secondary-400/15 to-pink-400/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/30 dark:to-secondary-900/30 border border-primary-200/50 dark:border-primary-700/30">
            <Sparkles className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{t('landing.hero.subtitle')}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-poppins leading-[1.1] tracking-tight mb-6">
            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-200 dark:to-white bg-clip-text text-transparent">
              {t('landing.hero.titleLine1')}
            </span>
            <br />
            <span className="bg-gradient-to-r from-primary-500 to-secondary-600 bg-clip-text text-transparent">
              {t('landing.hero.titleLine2')}
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-lg md:text-xl text-gray-500 dark:text-gray-400 leading-relaxed mb-10">
            {t('landing.hero.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => navigate('/onboard')}
              className="group flex items-center gap-2.5 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-500 to-secondary-600 rounded-2xl hover:shadow-xl hover:shadow-primary-500/25 transition-all duration-300 active:scale-[0.97]"
            >
              {t('landing.hero.primaryCTA')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="group flex items-center gap-2.5 px-8 py-4 text-base font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
            >
              <Play className="w-5 h-5 text-primary-600" />
              {t('landing.hero.secondaryCTA')}
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400 dark:text-gray-500 mb-16">
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> {t('landing.hero.trustNoCc')}</span>
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> {t('landing.hero.trustSetup')}</span>
            <span className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4" /> {t('landing.hero.trustK12')}</span>
          </div>

          {/* Dashboard Preview Mockup */}
          <div className="max-w-4xl mx-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl shadow-gray-200/50 dark:shadow-gray-900/50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-600 text-white">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center"><GraduationCap className="w-3.5 h-3.5" /></div>
                <span className="text-sm font-semibold">{t('landing.hero.mockup.schoolName')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs opacity-80">
                <Bell className="w-3.5 h-3.5" />
                <UserCheck className="w-3.5 h-3.5" />
              </div>
            </div>
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: t('landing.hero.mockup.students'), value: '847', icon: Users, trend: '+12', color: 'text-primary-600 bg-primary-50 dark:bg-primary-900/30' },
                  { label: t('landing.hero.mockup.attendance'), value: '94%', icon: ClipboardList, trend: '+2.1%', color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30' },
                  { label: t('landing.hero.mockup.gradesFilled'), value: '72%', icon: BarChart3, trend: '+8%', color: 'text-secondary-600 bg-secondary-50 dark:bg-secondary-900/30' },
                  { label: t('landing.hero.mockup.feesCollected'), value: '89%', icon: Wallet, trend: '+5%', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' },
                ].map((kpi, i) => {
                  const KIcon = kpi.icon;
                  return (
                    <div key={i} className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-7 h-7 rounded-lg ${kpi.color} flex items-center justify-center`}><KIcon className="w-3.5 h-3.5" /></div>
                        <span className="text-[10px] font-medium text-emerald-500">{'\u2191'}{kpi.trend}</span>
                      </div>
                      <div className="text-lg md:text-xl font-bold font-poppins">{kpi.value}</div>
                      <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">{kpi.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-end gap-1 h-12 mb-4 px-2">
                {[40, 55, 35, 65, 50, 70, 60, 80, 55, 75, 85, 90].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary-500 to-secondary-500 opacity-70 dark:opacity-50 transition-all duration-500" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {[ClipboardList, FileText, Calendar, MessageCircle, Wallet, BarChart3, Brain, GraduationCap].map((Icon, i) => (
                  <div key={i} className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <ChevronDown className="w-5 h-5 text-gray-300" />
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => <StatCounter key={i} value={s.value} label={s.label} />)}
        </div>
      </section>

      {/* Personas */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div ref={personaReveal.ref} className={`text-center mb-16 transition-all duration-700 ${personaReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.personasSection.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.personasSection.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map((p, i) => (
              <div
                key={i}
                className={`group relative flex flex-col p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 hover:-translate-y-1 transition-all duration-300 ${personaReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: personaReveal.visible ? `${i * 100}ms` : '0ms' }}
              >
                <div className="text-4xl mb-3">{p.emoji}</div>
                <h3 className="text-lg font-semibold font-poppins mb-1">{p.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">{p.desc}</p>
                <ul className="space-y-1.5 mb-5 flex-1">
                  {p.benefits.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <svg className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" viewBox="0 0 16 16" fill="none"><path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {b}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(p.route)}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* School Management \u2014 STAR Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-secondary-50/70 via-secondary-50/50 to-white dark:from-secondary-900/40 dark:via-secondary-900/30 dark:to-gray-950" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-secondary-300 dark:via-secondary-700 to-transparent" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div ref={schoolReveal.ref} className={`text-center mb-16 transition-all duration-700 ${schoolReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-sm font-semibold text-secondary-600 dark:text-secondary-400 uppercase tracking-wider mb-3">{t('landing.schoolMgmt.tag')}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.schoolMgmt.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.schoolMgmt.desc')}
            </p>
          </div>

          {moduleCategories.map((cat, ci) => (
            <div key={cat.key} className={`mb-12 last:mb-0 transition-all duration-700 ${schoolReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: schoolReveal.visible ? `${ci * 150}ms` : '0ms' }}>
              <h3 className="text-lg font-semibold font-poppins mb-4 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${cat.color}`} />
                {cat.label}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {cat.modules.map((mod, mi) => {
                  const MIcon = mod.icon;
                  return (
                    <div
                      key={mi}
                      className="group flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
                        <MIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{mod.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{mod.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/onboard/school-admin')}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-2xl hover:shadow-xl hover:shadow-secondary-500/25 transition-all duration-300 active:scale-[0.97]"
            >
              {t('landing.schoolMgmt.getStarted')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* See It In Action \u2014 Tabbed Interface */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div ref={actionReveal.ref} className={`text-center mb-12 transition-all duration-700 ${actionReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.seeItInAction.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.seeItInAction.desc')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {viewTabs.map((tab, i) => {
              const TIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveViewTab(i)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${activeViewTab === i ? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white shadow-lg shadow-primary-500/20' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <TIcon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeViewTab === 0 && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-primary-600" />
                <span className="text-sm font-semibold">{t('landing.seeItInAction.adminTitle')}</span>
              </div>
              <div className="p-5 md:p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  {[
                    { l: t('landing.seeItInAction.adminTotalStudents'), v: '1,247', c: 'text-primary-600' },
                    { l: t('landing.seeItInAction.adminTotalTeachers'), v: '63', c: 'text-emerald-600' },
                    { l: t('landing.seeItInAction.adminAttendance'), v: '96.2%', c: 'text-secondary-600' },
                    { l: t('landing.seeItInAction.adminRevenue'), v: '\u20A312.4M', c: 'text-amber-600' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 text-center">
                      <div className={`text-xl font-bold font-poppins ${s.c}`}>{s.v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {[
                    { icon: ClipboardList, label: t('landing.schoolMgmt.attendance') },
                    { icon: FileText, label: t('landing.schoolMgmt.reportCards') },
                    { icon: Wallet, label: t('landing.schoolMgmt.fees') },
                    { icon: Calendar, label: t('landing.schoolMgmt.timetables') },
                    { icon: Users, label: t('landing.seeItInAction.adminStaff') },
                    { icon: BarChart3, label: t('landing.schoolMgmt.analytics') },
                  ].map((m, i) => {
                    const MI = m.icon;
                    return (
                      <div key={i} className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                        <MI className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center">{m.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeViewTab === 1 && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold">{t('landing.seeItInAction.teacherTitle')}</span>
              </div>
              <div className="p-5 md:p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { l: t('landing.seeItInAction.teacherMyClasses'), v: '4', c: 'text-emerald-600' },
                    { l: t('landing.seeItInAction.teacherPendingGrades'), v: '28', c: 'text-amber-600' },
                    { l: t('landing.seeItInAction.teacherAvgScore'), v: '78%', c: 'text-secondary-600' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 text-center">
                      <div className={`text-xl font-bold font-poppins ${s.c}`}>{s.v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-300">{t('landing.seeItInAction.teacherGradeEntry')}</div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {[
                      { name: 'Amina K.', score: '85/100', badge: 'A' },
                      { name: 'Paul M.', score: '72/100', badge: 'B' },
                      { name: 'Fatou D.', score: '91/100', badge: 'A+' },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-gray-700 dark:text-gray-300">{s.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{s.score}</span>
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">{s.badge}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeViewTab === 2 && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-500" />
                <span className="text-sm font-semibold">{t('landing.seeItInAction.parentTitle')}</span>
              </div>
              <div className="p-5 md:p-6 space-y-4">
                <div className="p-4 rounded-xl bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border border-primary-100 dark:border-primary-800">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{t('landing.seeItInAction.parentChildName')}</p>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {[
                      { l: t('landing.seeItInAction.parentAverage'), v: '82%' },
                      { l: t('landing.seeItInAction.parentRank'), v: '#5/38' },
                      { l: t('landing.seeItInAction.parentAbsences'), v: '2' },
                    ].map((s, i) => (
                      <div key={i} className="text-center">
                        <div className="text-lg font-bold font-poppins text-gray-900 dark:text-white">{s.v}</div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: FileText, l: t('landing.seeItInAction.parentViewReportCard') },
                    { icon: MessageCircle, l: t('landing.seeItInAction.parentMessageTeacher') },
                    { icon: CreditCard, l: t('landing.seeItInAction.parentPayFees') },
                    { icon: Calendar, l: t('landing.seeItInAction.parentViewTimetable') },
                  ].map((a, i) => {
                    const AI = a.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300">
                        <AI className="w-4 h-4 text-secondary-500" />
                        {a.l}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeViewTab === 3 && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold">{t('landing.seeItInAction.accountingTitle')}</span>
              </div>
              <div className="p-5 md:p-6 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { l: t('landing.seeItInAction.accCollected'), v: '\u20A38.2M', c: 'text-emerald-600' },
                    { l: t('landing.seeItInAction.accPending'), v: '\u20A31.4M', c: 'text-amber-600' },
                    { l: t('landing.seeItInAction.accPayroll'), v: '\u20A33.8M', c: 'text-secondary-600' },
                    { l: t('landing.seeItInAction.accExpenses'), v: '\u20A3950K', c: 'text-rose-600' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 text-center">
                      <div className={`text-xl font-bold font-poppins ${s.c}`}>{s.v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-3">{t('landing.seeItInAction.accMonthlyOverview')}</p>
                  <div className="flex items-end gap-2 h-20">
                    {[60, 75, 45, 80, 90, 70, 85, 65, 95, 88, 72, 92].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-emerald-500 to-emerald-400 dark:from-emerald-600 dark:to-emerald-500" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-primary-50/30 to-white dark:from-gray-950 dark:via-primary-900/10 dark:to-gray-950" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div ref={feat1.ref} className={`text-center mb-16 transition-all duration-700 ${feat1.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.featuresSection.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.featuresSection.description')}
            </p>
          </div>

          <div ref={feat2.ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className={`group relative p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 hover:-translate-y-1 transition-all duration-300 ${feat2.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: feat2.visible ? `${i * 80}ms` : '0ms' }}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} text-white mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold font-poppins">{f.title}</h3>
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{f.badge}</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-16 border-y border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
        <div ref={trustReveal.ref} className={`max-w-5xl mx-auto px-5 sm:px-8 transition-all duration-700 ${trustReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-bold font-poppins bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">{'\u{1F512}'} {t('landing.trust.secure')}</div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('landing.trust.secureDesc')}</p>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold font-poppins bg-gradient-to-r from-emerald-600 to-primary-600 bg-clip-text text-transparent">{'\u{1F30D}'} {t('landing.trust.global')}</div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('landing.trust.globalDesc')}</p>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold font-poppins bg-gradient-to-r from-secondary-600 to-secondary-600 bg-clip-text text-transparent">{'\u26A1'} {t('landing.trust.fast')}</div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('landing.trust.fastDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-secondary-50/20 to-white dark:from-gray-950 dark:via-secondary-900/10 dark:to-gray-950" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div ref={testimonialReveal.ref} className={`text-center mb-16 transition-all duration-700 ${testimonialReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                className={`p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:shadow-lg transition-all duration-300 ${testimonialReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: testimonialReveal.visible ? `${i * 100}ms` : '0ms' }}
              >
                <div className="flex gap-0.5 mb-4 text-amber-400">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5">{'\u201C'}{testimonial.quote}{'\u201D'}</p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${testimonial.color} flex items-center justify-center text-white text-sm font-bold`}>
                    {testimonial.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold flex items-center gap-1.5">
                      {testimonial.name} <span className="text-base">{testimonial.flag}</span>
                    </p>
                    <p className="text-xs text-gray-400">
                      <span className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-[10px] font-medium">{testimonial.role}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-secondary-50/40 to-white dark:from-gray-950 dark:via-secondary-900/20 dark:to-gray-950" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div ref={pricingReveal.ref} className={`text-center mb-16 transition-all duration-700 ${pricingReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-500 dark:text-gray-400">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative flex flex-col p-6 rounded-2xl border bg-white dark:bg-gray-900 transition-all duration-300 ${plan.popular ? 'border-primary-400 dark:border-primary-600 shadow-xl shadow-primary-500/10 scale-[1.02]' : 'border-gray-100 dark:border-gray-800 hover:shadow-lg'} ${pricingReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: pricingReveal.visible ? `${i * 100}ms` : '0ms' }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-primary-500 to-secondary-600 rounded-full">
                    {t('landing.pricing.popular')}
                  </div>
                )}
                <h3 className="text-lg font-semibold font-poppins mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold font-poppins">{plan.price}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{plan.period}</span>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Check className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/onboard')}
                  className={`w-full py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${plan.popular ? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white hover:shadow-lg hover:shadow-primary-500/20' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div ref={stepsReveal.ref} className={`text-center mb-16 transition-all duration-700 ${stepsReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">{t('landing.steps.title')}</h2>
            <p className="text-lg text-gray-500 dark:text-gray-400">{t('landing.steps.subtitle')}</p>
          </div>

          <div className="space-y-8">
            {steps.map((s, i) => (
              <div
                key={i}
                className={`flex gap-6 items-start p-6 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 transition-all duration-500 ${stepsReveal.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                style={{ transitionDelay: stepsReveal.visible ? `${i * 120}ms` : '0ms' }}
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white font-bold font-poppins text-lg">
                  {s.num}
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-poppins mb-1">{s.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32">
        <div ref={ctaReveal.ref} className={`max-w-4xl mx-auto px-5 sm:px-8 text-center transition-all duration-700 ${ctaReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative p-12 md:p-16 rounded-3xl bg-gradient-to-br from-primary-500 to-secondary-600 text-white overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-white/10 rounded-full blur-xl" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-white/10 rounded-full blur-xl" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins tracking-tight mb-4">
                {t('landing.cta.title')}
              </h2>
              <p className="max-w-xl mx-auto text-lg opacity-90 mb-8">
                {t('landing.cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/onboard')}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold bg-white text-gray-900 rounded-2xl hover:bg-gray-100 transition-colors"
                >
                  {t('landing.cta.primary')}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold border-2 border-white/40 text-white rounded-2xl hover:bg-white/10 transition-colors"
                >
                  {t('landing.cta.secondary')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
            <div className="col-span-2">
              <a href="/" className="flex items-center gap-2.5 mb-4">
                <AppLogo size={32} />
                <span className="text-lg font-bold font-poppins bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  {t('common.appName')}
                </span>
              </a>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs mb-4">
                {t('landing.footer.tagline')}
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.balatchi.appacademia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
              >
                <Smartphone className="w-4 h-4" />
                {t('landing.footer.googlePlay')}
              </a>
              <div className="flex items-center gap-3 mt-4">
                {[
                  { icon: Twitter, href: 'https://twitter.com/appacademia', label: 'Twitter' },
                  { icon: Facebook, href: 'https://facebook.com/appacademia', label: 'Facebook' },
                  { icon: Linkedin, href: 'https://linkedin.com/company/appacademia', label: 'LinkedIn' },
                  { icon: Youtube, href: 'https://youtube.com/@appacademia', label: 'YouTube' },
                ].map((social) => {
                  const SIcon = social.icon;
                  return (
                    <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors" aria-label={social.label}>
                      <SIcon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('landing.footer.product')}</h4>
              <ul className="space-y-2.5">
                <li><button onClick={() => navigate('/demo')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.nav.tryDemo')}</button></li>
                <li><button onClick={() => navigate('/onboard')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.nav.getStarted')}</button></li>
                <li><button onClick={() => navigate('/onboard/school-admin')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.forSchools')}</button></li>
                <li><button onClick={() => navigate('/onboard/quick-student')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.forStudents')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('landing.footer.resources')}</h4>
              <ul className="space-y-2.5">
                <li><a href="https://myappacademia.com/blog" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.blog')}</a></li>
                <li><a href="https://myappacademia.com/docs/api" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.apiDocs')}</a></li>
                <li><a href="https://myappacademia.com/changelog" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.changelog')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('landing.footer.support')}</h4>
              <ul className="space-y-2.5">
                <li><a href="https://myappacademia.com/support.html" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.helpCenter')}</a></li>
                <li><a href="mailto:hello@myappacademia.com" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.contactUs')}</a></li>
                <li><button onClick={() => navigate('/app/support')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.liveChat')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2.5">
                <li><a href="https://myappacademia.com/privacy.html" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.privacy')}</a></li>
                <li><a href="https://myappacademia.com/terms.html" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.terms')}</a></li>
                <li><a href="https://myappacademia.com/delete-account.html" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.deleteAccount')}</a></li>
                <li><a href="https://myappacademia.com/privacy.html#childrens-privacy" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{t('landing.footer.coppa')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400 dark:text-gray-500">{t('landing.footer.rights')}</p>
            <a href="https://myappacademia.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 dark:text-gray-500 hover:text-primary-500 transition-colors">
              {t('landing.footer.poweredBy')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
